import { NavigationDrawerItemProps } from 'components/navigation_drawer/NavigationDrawerItem';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn as createStore } from 'zustand/traditional';

type NavigationDrawerState = {
    isDrawerExpanded: boolean;
    isFooterExpanded: boolean;
    isChatbotOpen: boolean;
    items: NavigationDrawerItemProps[];
    activeModule: NavigationDrawerItemProps | undefined;
    itemExpanded: NavigationDrawerItemProps | undefined;
    isHidden: boolean;
    setItems: (items: NavigationDrawerItemProps[]) => void;
    setActiveModule: (activeModule: NavigationDrawerItemProps | undefined) => void;
    setItemExpanded: (item: NavigationDrawerItemProps | undefined) => void;
    setDrawerExpanded: (isExpanded: boolean) => void;
    setFooterExpanded: (isExpanded: boolean) => void;
    setChatbotOpen: (isChatbotOpen: boolean) => void;
    setHidden: (isHidden: boolean) => void;
    toggleItemExpanded: (item: NavigationDrawerItemProps) => void;
    toggleDrawerExpanded: () => void;
    toggleFooterExpanded: () => void;
    toggleChatbotOpen: () => void;
    toggleHidden: () => void;
};

const useNavigationDrawerStore = createStore<NavigationDrawerState>(
    (set) => ({
        items: [],
        activeModule: undefined,
        itemExpanded: undefined,
        isDrawerExpanded: false,
        isFooterExpanded: false,
        isChatbotOpen: false,
        isHidden: false,
        setItems: (items) => set({ items }),
        setActiveModule: (activeModule) => set({ activeModule }),
        setItemExpanded: (itemExpanded) => set({ itemExpanded }),
        setDrawerExpanded: (isDrawerExpanded) => set({ isDrawerExpanded }),
        setFooterExpanded: (isFooterExpanded) => set({ isFooterExpanded }),
        setChatbotOpen: (isChatbotOpen) => set({ isChatbotOpen }),
        setHidden: (isHidden) => set({ isHidden }),

        toggleItemExpanded: (item) =>
            set((state) => ({
                itemExpanded: item === state.itemExpanded ? undefined : item,
            })),

        toggleFooterExpanded: () =>
            set((state) => ({ isFooterExpanded: !state.isFooterExpanded })),

        toggleDrawerExpanded: () =>
            set((state) =>
                state.isDrawerExpanded
                    ? {
                          isDrawerExpanded: false,
                          isFooterExpanded: false,
                          itemExpanded: undefined,
                      }
                    : {
                          isDrawerExpanded: true,
                          itemExpanded: state.activeModule,
                      },
            ),

        toggleChatbotOpen: () =>
            set((state) => ({ isChatbotOpen: !state.isChatbotOpen })),

        toggleHidden: () => set((state) => ({ isHidden: !state.isHidden })),
    }),
    shallow,
);

export const useNavigationDrawerValues = <TValues extends Record<string, unknown>>(
    values: (state: NavigationDrawerState) => TValues,
) => useNavigationDrawerStore(values, shallow);
